/**
 * Sentry Utilities
 *
 * Error logging utilities for web
 * Uses console logging in development, actual Sentry in production
 */

/**
 * Log to Sentry options
 */
export interface LogSentryOptions {
    error: Error;
    name: string;
    transactionName: string;
    extra?: Record<string, unknown>;
}

declare global {
    interface Window {
        ReactNativeWebView?: {
            postMessage(message: string): void;
        };
        Sentry?: {
            captureException?: (e: unknown) => void;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            withScope?: (cb: (scope: any) => void) => void;
            captureMessage?: (msg: string, opts: unknown) => void;
        };
    }
}

/**
 * Log error to Sentry (or console in development)
 *
 * On web, the actual Sentry SDK is configured at the app level
 * This utility provides a consistent interface for modules
 */
export const logSentry = (options: LogSentryOptions): void => {
    const { error, name, transactionName, extra } = options;

    // In browser environment, try to use Sentry if available
    if (globalThis?.window !== undefined) {
        const Sentry = globalThis?.window?.Sentry;
        if (Sentry?.captureException) {
            Sentry.withScope!(
                (scope: {
                    setTag: (key: string, value: string) => void;
                    setExtras: (extras: Record<string, unknown>) => void;
                }) => {
                    scope.setTag('name', name);
                    scope.setTag('transaction', transactionName);
                    if (extra) {
                        scope.setExtras(extra);
                    }
                    Sentry.captureException!(error);
                }
            );
            return;
        }
    }

    // Fallback to console logging
    console.error(`[${name}] ${transactionName}:`, error, extra);
};

/**
 * Capture exception
 */
export const captureException = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: Error,
    context?: Record<string, unknown>
): void => {
    logSentry({
        error,
        name: 'Exception',
        transactionName: 'captureException',
        extra: context,
    });
};

/**
 * Capture message
 */
export const captureMessage = (
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
    context?: Record<string, unknown>
): void => {
    const logMethods = {
        error: console.error,
        warning: console.warn,
        info: console.info,
    };
    const logMethod = logMethods[level];
    logMethod(`[Sentry ${level}]:`, message, context);

    // Try to use Sentry if available
    if (globalThis?.window !== undefined) {
        const Sentry = globalThis?.window?.Sentry;
        if (Sentry?.captureMessage) {
            Sentry.captureMessage(message, { level, extra: context });
        }
    }
};
